import { useState } from 'react';
import { Settings, Shield, Clock, FileText, AlertTriangle, Bell, Save } from 'lucide-react';
import { usePldConfig, useUpdatePldConfig } from '../../../hooks/usePld';

type TabType = 'pix-scheduler' | 'med' | 'pld' | 'coaf';

export default function ComplianceConfig() {
  const [activeTab, setActiveTab] = useState<TabType>('pld');
  
  const tabs = [
    { id: 'pix-scheduler' as TabType, label: 'PIX Agendado', icon: Clock },
    { id: 'med' as TabType, label: 'MED', icon: Shield },
    { id: 'pld' as TabType, label: 'PLD/FT', icon: AlertTriangle },
    { id: 'coaf' as TabType, label: 'COAF', icon: FileText },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="w-7 h-7 text-primary-400" />
          Configurações de Compliance
        </h1>
        <p className="text-surface-400 mt-1">
          Configure os módulos de conformidade BACEN do seu tenant
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-700 pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-600 text-white'
                : 'bg-surface-800 hover:bg-surface-700 text-surface-300'
            }`}
          >
            <tab.icon className="w-5 h-5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-surface-800 rounded-xl border border-surface-700">
        {activeTab === 'pix-scheduler' && <PixSchedulerConfig />}
        {activeTab === 'med' && <MedConfig />}
        {activeTab === 'pld' && <PldConfigTab />}
        {activeTab === 'coaf' && <CoafConfig />}
      </div>
    </div>
  );
}

function PixSchedulerConfig() {
  const [config, setConfig] = useState({
    isEnabled: true,
    maxScheduleDays: 365,
    notifyBeforeExecution: true,
    notifyHoursBefore: 24,
    autoRetryOnFailure: true,
    maxRetries: 3,
  });

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Clock className="w-5 h-5 text-blue-400" />
        Configurações do PIX Agendado
      </h2>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Módulo Ativo</p>
            <p className="text-sm text-surface-400">Permitir agendamento de PIX</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.isEnabled}
              onChange={(e) => setConfig(prev => ({ ...prev, isEnabled: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Máximo de Dias para Agendamento</label>
          <input
            type="number"
            value={config.maxScheduleDays}
            onChange={(e) => setConfig(prev => ({ ...prev, maxScheduleDays: Number(e.target.value) }))}
            min={1}
            max={365}
            className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg"
          />
          <p className="text-sm text-surface-400 mt-1">Limite BACEN: 365 dias</p>
        </div>

        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Notificar Antes da Execução</p>
            <p className="text-sm text-surface-400">Enviar lembrete ao cliente</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.notifyBeforeExecution}
              onChange={(e) => setConfig(prev => ({ ...prev, notifyBeforeExecution: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        {config.notifyBeforeExecution && (
          <div>
            <label className="block text-sm font-medium mb-2">Horas Antes para Notificar</label>
            <input
              type="number"
              value={config.notifyHoursBefore}
              onChange={(e) => setConfig(prev => ({ ...prev, notifyHoursBefore: Number(e.target.value) }))}
              min={1}
              max={72}
              className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg"
            />
          </div>
        )}

        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Retry Automático em Falha</p>
            <p className="text-sm text-surface-400">Tentar novamente se falhar por saldo insuficiente</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.autoRetryOnFailure}
              onChange={(e) => setConfig(prev => ({ ...prev, autoRetryOnFailure: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>

      <button className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium">
        <Save className="w-5 h-5" />
        Salvar Configurações
      </button>
    </div>
  );
}

function MedConfig() {
  const [config, setConfig] = useState({
    isEnabled: true,
    maxDaysToRequest: 80,
    requireDocumentsForFraud: true,
    autoRejectExpired: true,
    notifyOnNewRequest: true,
    notifyOnStatusChange: true,
    prioritizeFraudCases: true,
  });

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Shield className="w-5 h-5 text-green-400" />
        Configurações do MED (Mecanismo Especial de Devolução)
      </h2>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Módulo Ativo</p>
            <p className="text-sm text-surface-400">Permitir solicitações de devolução</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.isEnabled}
              onChange={(e) => setConfig(prev => ({ ...prev, isEnabled: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Prazo Máximo para Solicitação (dias)</label>
          <input
            type="number"
            value={config.maxDaysToRequest}
            onChange={(e) => setConfig(prev => ({ ...prev, maxDaysToRequest: Number(e.target.value) }))}
            min={1}
            max={80}
            className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg"
          />
          <p className="text-sm text-surface-400 mt-1">Limite BACEN: 80 dias após a transação</p>
        </div>

        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Exigir Documentos para Fraude</p>
            <p className="text-sm text-surface-400">Obrigar upload de B.O. em casos de fraude</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.requireDocumentsForFraud}
              onChange={(e) => setConfig(prev => ({ ...prev, requireDocumentsForFraud: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Rejeitar Automaticamente Expirados</p>
            <p className="text-sm text-surface-400">Rejeitar solicitações não analisadas após prazo</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.autoRejectExpired}
              onChange={(e) => setConfig(prev => ({ ...prev, autoRejectExpired: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Priorizar Casos de Fraude</p>
            <p className="text-sm text-surface-400">Mostrar fraudes primeiro na fila</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.prioritizeFraudCases}
              onChange={(e) => setConfig(prev => ({ ...prev, prioritizeFraudCases: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>

      <button className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium">
        <Save className="w-5 h-5" />
        Salvar Configurações
      </button>
    </div>
  );
}

function PldConfigTab() {
  const { data: config, isLoading } = usePldConfig();
  const updateMutation = useUpdatePldConfig();
  
  const [formData, setFormData] = useState({
    isEnabled: true,
    autoEscalationHours: 48,
    notifyOnHighSeverity: true,
    notifyOnCriticalSeverity: true,
    blockOnCritical: false,
    retentionDays: 365,
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-700 rounded w-1/4"></div>
          <div className="h-32 bg-surface-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-yellow-400" />
        Configurações PLD/FT
      </h2>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Módulo Ativo</p>
            <p className="text-sm text-surface-400">Ativar monitoramento PLD/FT</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isEnabled}
              onChange={(e) => setFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Tempo para Escalação Automática (horas)</label>
          <input
            type="number"
            value={formData.autoEscalationHours}
            onChange={(e) => setFormData(prev => ({ ...prev, autoEscalationHours: Number(e.target.value) }))}
            min={1}
            max={168}
            className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg"
          />
        </div>

        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Bloquear Transações Críticas</p>
            <p className="text-sm text-surface-400">Bloquear automaticamente alertas CRITICAL</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.blockOnCritical}
              onChange={(e) => setFormData(prev => ({ ...prev, blockOnCritical: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Retenção de Dados (dias)</label>
          <input
            type="number"
            value={formData.retentionDays}
            onChange={(e) => setFormData(prev => ({ ...prev, retentionDays: Number(e.target.value) }))}
            min={365}
            max={3650}
            className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg"
          />
          <p className="text-sm text-surface-400 mt-1">Mínimo exigido: 365 dias (BACEN)</p>
        </div>
      </div>

      <button
        onClick={() => updateMutation.mutate(formData)}
        disabled={updateMutation.isPending}
        className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium disabled:opacity-50"
      >
        <Save className="w-5 h-5" />
        {updateMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
      </button>
    </div>
  );
}

function CoafConfig() {
  const [config, setConfig] = useState({
    isEnabled: true,
    autoCreateFromPld: true,
    minSeverityForAuto: 'HIGH',
    deadlineWarningDays: 5,
    requireApproval: true,
    notifyOnDeadline: true,
    xmlVersion: '2.0',
  });

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <FileText className="w-5 h-5 text-purple-400" />
        Configurações COAF
      </h2>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Módulo Ativo</p>
            <p className="text-sm text-surface-400">Ativar comunicações ao COAF</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.isEnabled}
              onChange={(e) => setConfig(prev => ({ ...prev, isEnabled: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Criar Automaticamente de Alertas PLD</p>
            <p className="text-sm text-surface-400">Gerar comunicação quando alerta for confirmado</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.autoCreateFromPld}
              onChange={(e) => setConfig(prev => ({ ...prev, autoCreateFromPld: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        {config.autoCreateFromPld && (
          <div>
            <label className="block text-sm font-medium mb-2">Severidade Mínima para Auto-criação</label>
            <select
              value={config.minSeverityForAuto}
              onChange={(e) => setConfig(prev => ({ ...prev, minSeverityForAuto: e.target.value }))}
              className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg"
            >
              <option value="MEDIUM">Média</option>
              <option value="HIGH">Alta</option>
              <option value="CRITICAL">Crítica</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2">Dias de Aviso Antes do Prazo</label>
          <input
            type="number"
            value={config.deadlineWarningDays}
            onChange={(e) => setConfig(prev => ({ ...prev, deadlineWarningDays: Number(e.target.value) }))}
            min={1}
            max={30}
            className="w-full px-4 py-3 bg-surface-700 border border-surface-600 rounded-lg"
          />
        </div>

        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Exigir Aprovação</p>
            <p className="text-sm text-surface-400">Comunicações precisam de aprovação antes do envio</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.requireApproval}
              onChange={(e) => setConfig(prev => ({ ...prev, requireApproval: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
          <div>
            <p className="font-medium">Notificar Prazos</p>
            <p className="text-sm text-surface-400">Alertar quando prazo estiver próximo</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.notifyOnDeadline}
              onChange={(e) => setConfig(prev => ({ ...prev, notifyOnDeadline: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-600 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>

      <button className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium">
        <Save className="w-5 h-5" />
        Salvar Configurações
      </button>
    </div>
  );
}
